import React from 'react';
import { useSearchParams } from 'react-router-dom';

import { WithRouterProps, withRouter } from './with-router';

const DISPLAY_NAME = 'withQueryParams';

/**
 * @deprecated use useParams instead
 */
export type WithQueryParamsInjectedProps<T> = WithRouterProps & {
    setQueryParams(params: T): void;
    queryParams: T;
};

/**
 * @description injects `queryParams` and `setQueryParams` to read and manipulate query string params
 * @deprecated use useSearchParams instead
 * @example
 * ```
 * import withQueryParams, { WithQueryParamsInjectedProps } from '@portal/common/utility/hoc/with-query-params';
 *
 * type QueryParams = {
 *     page?: string;
 *     per_page?: string;
 *     search?: string;
 *     order_by?: string;
 * };
 *
 * export interface Props extends WithQueryParamsInjectedProps<QueryParams> {
 *    organizationId: number;
 * }
 *
 * const Component: React.SFC<Props> = ({ queryParams, setQueryParams }) => {
 *     // queryParams = { page, per_page, search, order_by };
 *     setQueryParams({
 *         ...queryParams,
 *         page: 2,
 *     })
 * }
 *
 * export default withQueryParams(Component);
 * ```
 */
export default function <BaseComponentProps extends Partial<WithQueryParamsInjectedProps<{}>>>(
    BaseComponent: React.ComponentType<BaseComponentProps>
) {
    type HocProps = BaseComponentProps;

    const Hoc: React.SFC<HocProps> = (props) => {
        const [searchParams, setSearchParams] = useSearchParams();

        const setQueryParams = (params: object) => {
            setSearchParams(params);
        };

        const getQueryParams = (): object => Object.fromEntries(searchParams);

        return (
            <BaseComponent
                setQueryParams={setQueryParams}
                queryParams={getQueryParams()}
                {...props}
            />
        );
    };

    Hoc.displayName = `${DISPLAY_NAME}(${BaseComponent.name})`;

    return withRouter(Hoc);
}
